// useTransport.ts
// manages transport data, updates

import { create } from "zustand";
import {
  TransportData,
  ActiveTransportMode,
  mapViewport,
  TripTransport,
} from "@/types";

interface TransportState {
  activeTransportMode: ActiveTransportMode;
  setActiveTransportMode: (mode: ActiveTransportMode) => void;
  activeTransportButtonId: string | null;
  setActiveTransportButtonId: (id: string | null) => void;
  transportData: TransportData;
  setTransportData: (data: TransportData) => void;
  isDateConflict: boolean;
  setIsDateConflict: (isConflict: boolean) => void;
  overViewMapViewport: mapViewport;
  setOverViewMapViewport: (viewport: mapViewport) => void;
  accommodationMapViewport: mapViewport;
  setAccommodationMapViewport: (viewport: mapViewport) => void;
  accommodationRadius: number;
  setAccommodationRadius: (radius: number) => void;
  accommodationBounds: {
    _sw: { lng: number; lat: number };
    _ne: { lng: number; lat: number };
  } | null;
  setAccommodationBounds: (
    bounds: {
      _sw: { lng: number; lat: number };
      _ne: { lng: number; lat: number };
    } | null
  ) => void;
  getFlightForLocation: (
    transport: TripTransport[],
    locationId: string
  ) => TripTransport | null;
  getEffectiveArrivalDate: (
    transport: TripTransport[],
    locationId: string
  ) => string | null;
}

const useTransportStore = create<TransportState>((set) => ({
  activeTransportMode: "flights",
  setActiveTransportMode: (mode: ActiveTransportMode) =>
    set({ activeTransportMode: mode }),
  activeTransportButtonId: null,
  setActiveTransportButtonId: (id: string | null) =>
    set({ activeTransportButtonId: id }),
  transportData: {
    selectedLocation: null,
    locationAbove: null,
    locationBelow: null,
  },
  isDateConflict: false,
  setIsDateConflict: (isConflict: boolean) =>
    set({ isDateConflict: isConflict }),
  overViewMapViewport: {
    longitude: 144.9631,
    latitude: -37.8136,
    zoom: 12,
  },
  accommodationMapViewport: {
    longitude: 144.9631,
    latitude: -37.8136,
    zoom: 8,
  },
  accommodationRadius: 50000,
  setAccommodationRadius: (radius: number) =>
    set({ accommodationRadius: radius }),
  accommodationBounds: null,
  setAccommodationBounds: (
    bounds: {
      _sw: { lng: number; lat: number };
      _ne: { lng: number; lat: number };
    } | null
  ) => set({ accommodationBounds: bounds }),
  setTransportData: (data: TransportData) => set({ transportData: data }),
  setOverViewMapViewport: (viewport: any) =>
    set({ overViewMapViewport: viewport }),
  setAccommodationMapViewport: (viewport: mapViewport) =>
    set({ accommodationMapViewport: viewport }),

  getFlightForLocation: (transport: TripTransport[], locationId: string) => {
    if (!transport || !locationId) return null;
    const transportItem = transport.find(
      (item) => item.toLocationId === locationId
    );
    return transportItem || null;
  },

  getEffectiveArrivalDate: (transport: TripTransport[], locationId: string) => {
    if (!transport || !locationId) return null;
    const transportItem = transport.find(
      (item) => item.toLocationId === locationId
    );
    if (
      !transportItem ||
      !transportItem.legs ||
      transportItem.legs.length === 0
    )
      return null;
    return (
      transportItem.legs[transportItem.legs.length - 1]?.arrival.time.utc ||
      null
    );
  },
}));

export default useTransportStore;
